// src/components/InternalGoalListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import GoalCard from './GoalCard';
import InitiativeCard from './InitiativeCard';
import CreateEntityModal from './CreateEntityModal';
import { GOAL_STATUS_OPTIONS, INITIATIVE_STATUS_OPTIONS } from '../constants';
import { ROUTES } from '../constants/routes';
import { DepartmentSelector, usePersistedFilters, DropdownCheckbox, ToggleButton, SingleSelectDropdown, Tooltip } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { RiInformationLine, RiAddLine } from 'react-icons/ri';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

// LocalStorage key for Eisenhower Matrix visibility (shared across views)
const EISENHOWER_VISIBLE_KEY = 'cortex-eisenhower-visible';

const InternalGoalListView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);

    const [goalsList, setGoalsList] = useState([]);
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [errorGoals, setErrorGoals] = useState(null);

    const [initiativesList, setInitiativesList] = useState([]);
    const [loadingInitiatives, setLoadingInitiatives] = useState(false);
    const [errorInitiatives, setErrorInitiatives] = useState(null);

    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    // Goal status filter - persisted for card navigation
    const [statusFilter, setStatusFilter] = usePersistedFilters('statusFilter', '');
    
    // Initiative status filter (persisted for card navigation)
    const [initiativeStatusFilter, setInitiativeStatusFilter] = usePersistedFilters('initiativeStatusFilter', '');
    // Show blocked initiatives filter (open initiatives with blocking issues)
    const [showBlockedInitiatives, setShowBlockedInitiatives] = usePersistedFilters('showBlockedInitiatives', false);
    
    // Related Clients filter
    const [relatedClients, setRelatedClients] = useState([]);
    const [selectedClients, setSelectedClients] = usePersistedFilters('selectedClients', []);
    
    // Create modal state
    const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
    const [showCreateInitiativeModal, setShowCreateInitiativeModal] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [allClients, setAllClients] = useState([]);
    
    // Eisenhower Matrix filters - persisted
    const [minImportanceFilter, setMinImportanceFilter] = usePersistedFilters('minImportance', 0);
    const [minUrgencyFilter, setMinUrgencyFilter] = usePersistedFilters('minUrgency', 0);

    // Eisenhower Matrix visibility - persisted to localStorage (shared across views)
    const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(() => {
        const saved = localStorage.getItem(EISENHOWER_VISIBLE_KEY);
        return saved === 'true';
    });
    
    const toggleEisenhowerMatrix = () => {
        setShowEisenhowerMatrix(prev => {
            const newState = !prev;
            localStorage.setItem(EISENHOWER_VISIBLE_KEY, String(newState));
            return newState;
        });
    };

    const hasActiveFilters = selectedDris.length > 0 || statusFilter !== '' || initiativeStatusFilter !== '' || showBlockedInitiatives || minImportanceFilter > 0 || minUrgencyFilter > 0 || selectedClients.length > 0;
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setStatusFilter('');
        setInitiativeStatusFilter('');
        setShowBlockedInitiatives(false);
        setMinImportanceFilter(0);
        setMinUrgencyFilter(0);
        setSelectedClients([]);
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    const loadRelatedClients = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        try {
            const clientResult = await apiService.getDepartmentRelatedClients(selectedDepartment, dateRange.start, dateRange.end);
            setRelatedClients(clientResult.map(r => r.clientName).filter(Boolean));
        } catch (err) {
            console.error('Error loading related clients:', err);
        }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadRelatedClients();
        }
    }, [selectedDepartment, dateRange, loadRelatedClients]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleClientChange = (client) => {
        setSelectedClients(prev =>
            prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]
        );
    };

    // Load departments and supporting data for create modals
    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        setLoadingDepartments(true);
        try {
            const [deptResult, staffResult, clientsResult] = await Promise.all([
                apiService.getDepartmentList(),
                apiService.getAllStaff(false).catch(() => []),
                apiService.getAllClientsWithStats().catch(() => [])
            ]);
            
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            // Staff list for create modal DRI selector
            setStaffList(staffResult.map(s => s.staffName || s.name).filter(Boolean));
            
            // Clients list for create modal (department goals need a client)
            setAllClients(clientsResult.map(c => ({ id: c.clientId || c.name, name: c.name })).filter(c => c.id && c.name));
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/goals`);
    };

    const loadDepartmentGoals = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingGoals(true); setErrorGoals(null);
        try {
            const goals = await apiService.getDepartmentGoalsList(
                selectedDepartment,
                dateRange.start,
                dateRange.end,
                selectedDris.length > 0 ? selectedDris : null,
                statusFilter || null,
                selectedClients.length > 0 ? selectedClients : null
            );
            setGoalsList(goals);
        } catch (err) {
            setErrorGoals(err.message); setGoalsList([]);
        } finally { setLoadingGoals(false); }
    }, [apiService, selectedDepartment, dateRange, selectedDris, statusFilter, selectedClients]);

    const loadDepartmentInitiatives = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingInitiatives(true); setErrorInitiatives(null);
        try {
            const initiatives = await apiService.getDepartmentInitiativesList(
                selectedDepartment,
                dateRange.start,
                dateRange.end,
                selectedDris.length > 0 ? selectedDris : null,
                statusFilter || null,
                selectedClients.length > 0 ? selectedClients : null
            );
            setInitiativesList(initiatives);
        } catch (err) {
            setErrorInitiatives(err.message); setInitiativesList([]);
        } finally { setLoadingInitiatives(false); }
    }, [apiService, selectedDepartment, dateRange, selectedDris, statusFilter, selectedClients]);

    // Handle goal status update
    const handleGoalStatusChange = useCallback(async (goalId, newStatus) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeStatus(goalId, newStatus);
            // Update local state
            setGoalsList(prevList =>
                prevList.map(goal =>
                    goal.id === goalId ? { ...goal, status: newStatus } : goal
                )
            );
        } catch (err) {
            console.error('Error updating goal status:', err);
        }
    }, [apiService]);

    // Handle goal target date update
    const handleGoalTargetDateChange = useCallback(async (goalId, newDate) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeProperty(goalId, 'target_date', newDate);
            // Update local state
            setGoalsList(prevList =>
                prevList.map(goal =>
                    goal.id === goalId ? { ...goal, targetDate: newDate, target_date: newDate } : goal
                )
            );
        } catch (err) {
            console.error('Error updating goal target date:', err);
        }
    }, [apiService]);

    // Handle initiative status update
    const handleInitiativeStatusChange = useCallback(async (initiativeId, newStatus) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeStatus(initiativeId, newStatus);
            // Update local state
            setInitiativesList(prevList =>
                prevList.map(init =>
                    init.id === initiativeId ? { ...init, status: newStatus } : init
                )
            );
        } catch (err) {
            console.error('Error updating initiative status:', err);
        }
    }, [apiService]);

    // Handle initiative target date update
    const handleInitiativeTargetDateChange = useCallback(async (initiativeId, newDate) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeProperty(initiativeId, 'target_date', newDate);
            // Update local state
            setInitiativesList(prevList =>
                prevList.map(init =>
                    init.id === initiativeId ? { ...init, targetDate: newDate, target_date: newDate } : init
                )
            );
        } catch (err) {
            console.error('Error updating initiative target date:', err);
        }
    }, [apiService]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentGoals();
            loadDepartmentInitiatives();
        }
    }, [selectedDepartment, loadDepartmentGoals, loadDepartmentInitiatives, statusFilter]);
    
    // Handlers for entity creation
    const handleGoalCreated = (newGoal) => {
        // Format the goal with expected fields
        const formattedGoal = {
            ...newGoal,
            assigned_to: newGoal.assigned_to || newGoal.assignee,
            importanceScore: 0,
            urgencyScore: 1.5,
        };
        setGoalsList(prev => [formattedGoal, ...prev]);
        setShowCreateGoalModal(false);
    };

    const handleInitiativeCreated = (newInitiative) => {
        // Format the initiative with expected fields
        const formattedInitiative = {
            ...newInitiative,
            assigned_to: newInitiative.assigned_to || newInitiative.assignee,
            importanceScore: 0,
            urgencyScore: 1.5,
        };
        setInitiativesList(prev => [formattedInitiative, ...prev]);
        setShowCreateInitiativeModal(false);
    };

    // Custom API service wrapper for creating department entities (when "Internal" is selected)
    const departmentApiService = useMemo(() => {
        if (!apiService || !selectedDepartment) return null;
        return new Proxy(apiService, {
            get(target, prop) {
                if (prop === 'createGoal') {
                    return (clientId, data) => target.createDepartmentGoal(selectedDepartment, data);
                }
                if (prop === 'createInitiative') {
                    return (clientId, data) => target.createDepartmentInitiative(selectedDepartment, data);
                }
                const value = target[prop];
                if (typeof value === 'function') {
                    return value.bind(target);
                }
                return value;
            }
        });
    }, [apiService, selectedDepartment]);

    // Filter goals by min importance and urgency
    const filteredGoals = useMemo(() => {
        return goalsList.filter(goal =>
            (goal.importanceScore || 0) >= minImportanceFilter &&
            (goal.urgencyScore || 0) >= minUrgencyFilter
        );
    }, [goalsList, minImportanceFilter, minUrgencyFilter]);

    // Filter initiatives locally for "blocked" status
    // Blocked = open initiatives with blocking issues (blockingIssuesCount > 0)
    // Filter initiatives:
    // - showBlockedInitiatives: open initiatives with issues blocking them (blockingIssuesCount > 0)
    // - initiativeStatusFilter: filter by specific status
    // - minImportanceFilter/minUrgencyFilter: filter by importance and urgency scores
    const filteredInitiatives = useMemo(() => {
        let filtered = initiativesList.filter(init =>
            (init.importanceScore || 0) >= minImportanceFilter &&
            (init.urgencyScore || 0) >= minUrgencyFilter
        );
        if (showBlockedInitiatives) {
            // Show open initiatives that have blocking issues
            return filtered.filter(init => 
                init.status === 'open' && init.blockingIssuesCount > 0
            );
        }
        if (initiativeStatusFilter) {
            return filtered.filter(init => init.status === initiativeStatusFilter);
        }
        return filtered;
    }, [initiativesList, initiativeStatusFilter, showBlockedInitiatives, minImportanceFilter, minUrgencyFilter]);
    
    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[
                    { id: 'context', label: 'Context', filters: [{ key: 'department' }, { key: 'client' }] },
                    { id: 'goals', label: 'Goals & Initiatives', filters: [{ key: 'goalStatus' }, { key: 'initiativeStatus' }, { key: 'blockedInitiatives' }] }
                ]}
                filters={{ department: selectedDepartment, client: selectedClients, goalStatus: statusFilter, initiativeStatus: initiativeStatusFilter, blockedInitiatives: showBlockedInitiatives }}
                filterConfigs={{
                    department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loadingDepartments} /> },
                    client: { label: 'Client', render: () => <DropdownCheckbox options={relatedClients} selectedOptions={selectedClients} onChange={handleClientChange} title="Client" /> },
                    goalStatus: { label: 'Goal Status', render: () => <SingleSelectDropdown id="goalStatusFilter" label="Goal Status" value={statusFilter} onChange={setStatusFilter} options={GOAL_STATUS_OPTIONS} allLabel="All" /> },
                    initiativeStatus: { label: 'Initiative Status', render: () => <SingleSelectDropdown id="initiativeStatusFilter" label="Init. Status" value={initiativeStatusFilter} onChange={(val) => { setInitiativeStatusFilter(val); setShowBlockedInitiatives(false); }} options={INITIATIVE_STATUS_OPTIONS} allLabel="All" /> },
                    blockedInitiatives: { label: 'Blocked Initiatives', render: () => <Tooltip content="Shows open initiatives that have issues blocking them"><ToggleButton label="Blocked Init. Only" isActive={showBlockedInitiatives} onClick={() => { setShowBlockedInitiatives(!showBlockedInitiatives); if (!showBlockedInitiatives) setInitiativeStatusFilter(''); }} /></Tooltip> }
                }}
                activeFilterChips={[
                    ...(selectedClients.length > 0 ? [{ label: 'Clients', value: selectedClients.length === 1 ? selectedClients[0] : `${selectedClients.length} selected`, onRemove: () => setSelectedClients([]) }] : []),
                    ...(statusFilter ? [{ label: 'Goal', value: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1), onRemove: () => setStatusFilter('') }] : []),
                    ...(initiativeStatusFilter ? [{ label: 'Initiative', value: initiativeStatusFilter.charAt(0).toUpperCase() + initiativeStatusFilter.slice(1), onRemove: () => setInitiativeStatusFilter('') }] : []),
                    ...(showBlockedInitiatives ? [{ label: 'Blocked', value: 'Yes', onRemove: () => setShowBlockedInitiatives(false) }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            {/* Eisenhower Matrix Section - Collapsible */}
            <div className="mb-8 bg-white rounded-lg shadow overflow-hidden">
                <button
                    onClick={toggleEisenhowerMatrix}
                    className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 border-b border-gray-200"
                >
                    <div className="flex items-center">
                        <svg 
                            className={`h-5 w-5 text-gray-500 mr-3 transition-transform duration-200 ${showEisenhowerMatrix ? 'rotate-90' : ''}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                        >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Priority Matrix</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Goals & Initiatives prioritized by urgency and importance</p>
                        </div>
                    </div>
                </button>
                
                {showEisenhowerMatrix && (
                    <div className="p-4 md:p-6">
                        <EisenhowerMatrixView 
                            departmentId={selectedDepartment} 
                            clientId={null} 
                            typeFilter={['Goal', 'Initiative']}
                            driFilter={selectedDris}
                            clientFilter={selectedClients}
                            minImportanceFilter={minImportanceFilter}
                            minUrgencyFilter={minUrgencyFilter}
                            goalStatusFilter={statusFilter}
                            initiativeStatusFilter={initiativeStatusFilter}
                            showBlockedInitiatives={showBlockedInitiatives}
                        />
                    </div>
                )}
            </div>

            {/* Goals and Initiatives Card Grid - 50/50 split */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Goals */}
                <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>Department Goals</h3>
                            <Tooltip content="Goals are shown if they have a related communication within the selected date range.">
                                <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                            </Tooltip>
                        </div>
                        <button
                            onClick={() => setShowCreateGoalModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-colors"
                            style={{ backgroundColor: 'var(--pallium-primary-purple)' }}
                        >
                            <RiAddLine className="w-4 h-4" />
                            New Goal
                        </button>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                        Goals related to {selectedDepartment || 'this department'}
                    </p>
                    
                    {loadingGoals ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading goals...</div>
                    ) : errorGoals ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error: {errorGoals}</div>
                    ) : filteredGoals.length === 0 ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>No goals found matching the current filters.</div>
                    ) : (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                            {filteredGoals.map((goal, index) => (
                                <GoalCard
                                    key={goal.id || index}
                                    goal={{
                                        ...goal,
                                        goalName: goal.name,
                                        goalId: goal.id,
                                        goalStatus: goal.status,
                                        dri: goal.assigned_to,
                                        targetDate: goal.targetDate || goal.target_date
                                    }}
                                    clientId={null}
                                    onClick={() => navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/goals/${goal.id}`)}
                                    editable={true}
                                    statusOptions={GOAL_STATUS_OPTIONS}
                                    onStatusChange={handleGoalStatusChange}
                                    onTargetDateChange={handleGoalTargetDateChange}
                                    data-testid="goal-card"
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Initiatives */}
                <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                                {showBlockedInitiatives ? "Blocked Initiatives" : "Department Initiatives"}
                            </h3>
                            <Tooltip content="Initiatives are shown if they have a related communication within the selected date range.">
                                <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                            </Tooltip>
                        </div>
                        <button
                            onClick={() => setShowCreateInitiativeModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-colors"
                            style={{ backgroundColor: 'var(--pallium-info-cyan)' }}
                        >
                            <RiAddLine className="w-4 h-4" />
                            New Initiative
                        </button>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                        {showBlockedInitiatives ? "Open initiatives with blocking issues" : `Initiatives related to ${selectedDepartment || 'this department'}`}
                    </p>
                    
                    {loadingInitiatives ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading initiatives...</div>
                    ) : errorInitiatives ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error: {errorInitiatives}</div>
                    ) : filteredInitiatives.length === 0 ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>No initiatives found matching the current filters.</div>
                    ) : (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                            {filteredInitiatives.map((initiative, index) => (
                                <InitiativeCard
                                    key={initiative.id || index}
                                    initiative={{
                                        ...initiative,
                                        dri: initiative.assigned_to
                                    }}
                                    clientId={null}
                                    onClick={() => navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/initiatives/${initiative.id}`)}
                                    editable={true}
                                    statusOptions={INITIATIVE_STATUS_OPTIONS}
                                    onStatusChange={handleInitiativeStatusChange}
                                    onTargetDateChange={handleInitiativeTargetDateChange}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Goal Modal - Single step with optional account selection */}
            {showCreateGoalModal && (
                <CreateEntityModal
                    isOpen={true}
                    onClose={() => setShowCreateGoalModal(false)}
                    entityType="goal"
                    clientId={null}
                    apiService={apiService}
                    onSuccess={handleGoalCreated}
                    staffList={staffList}
                    clientsList={allClients}
                    departmentId={selectedDepartment}
                    departmentApiService={departmentApiService}
                />
            )}

            {/* Create Initiative Modal - Single step with optional account selection */}
            {showCreateInitiativeModal && (
                <CreateEntityModal
                    isOpen={true}
                    onClose={() => setShowCreateInitiativeModal(false)}
                    entityType="initiative"
                    clientId={null}
                    apiService={apiService}
                    onSuccess={handleInitiativeCreated}
                    staffList={staffList}
                    goalsList={goalsList.map(g => ({ id: g.id, name: g.name }))}
                    clientsList={allClients}
                    departmentId={selectedDepartment}
                    departmentApiService={departmentApiService}
                />
            )}
        </>
    );
};

export default InternalGoalListView;